import { hasAccess } from "@woven/contracts";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/context";
import { DISCORD_URL, GITHUB_URL } from "../lib/links";
import { BrandMark } from "./ui/BrandMark";
import { DiscordMark } from "./ui/DiscordMark";
import { GithubMark } from "./ui/GithubMark";
import { NodeField } from "./ui/NodeField";
import { DocumentTitle } from "./DocumentTitle";
import { SignInModal } from "./SignInModal";

const PUBLIC_LINKS = [
  { to: "/docs", label: "Docs" },
  { to: "/pricing", label: "Pricing" },
  { to: "/network", label: "Network" },
] as const;

export function PublicLayout() {
  const { user, loading, access, error, accessRequested, signOut } = useAuth();
  const canOpenConsole = Boolean(
    user && !loading && !error && accessRequested !== null && hasAccess(access, "lite")
  );
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const navigate = useNavigate();

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    setSignOutError(false);
    try {
      await signOut();
      setMenuOpen(false);
      navigate("/");
    } catch {
      setSignOutError(true);
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="app-shell">
      <DocumentTitle />
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="site-header-inner">
          <Link to="/" className="brand" aria-label="Woven home" onClick={() => setMenuOpen(false)}>
            <span className="brand-field" aria-hidden="true">
              <NodeField
                nodeCount={12}
                connectDistance={1.3}
                fieldWidth={3.2}
                fieldHeight={1.3}
                fieldDepth={1.3}
                cameraDistance={3.4}
                pointSize={0.045}
              />
            </span>
            <span className="brand-content">
              <BrandMark size={26} />
              <span className={pathname === "/" ? "brand-name brand-name--active" : "brand-name"}>
                woven.host
              </span>
            </span>
          </Link>

          <nav className="site-nav" aria-label="Primary">
            <ul className="site-nav-list">
              <li>
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="nav-link nav-link--icon"
                  aria-label="GitHub"
                >
                  <GithubMark size={23} />
                </a>
              </li>
              <li>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="nav-link nav-link--icon"
                  aria-label="Discord"
                >
                  <DiscordMark size={23} />
                </a>
              </li>
              {PUBLIC_LINKS.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      isActive ? "nav-link nav-link--active" : "nav-link"
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-header-actions">
            {canOpenConsole ? (
              <Link to="/console" className="btn btn--primary">
                Open Console
              </Link>
            ) : (
              <>
                {!user ? (
                  <button
                    type="button"
                    className="nav-link nav-link-button"
                    onClick={() => setSignInOpen(true)}
                    disabled={loading}
                  >
                    Sign In
                  </button>
                ) : (
                  <button
                    type="button"
                    className="nav-link nav-link-button"
                    onClick={() => void handleSignOut()}
                    disabled={signingOut}
                  >
                    {signingOut ? "Signing Out…" : "Sign Out"}
                  </button>
                )}
                <Link to="/sign-up" className="btn btn--primary">
                  Request Access
                </Link>
              </>
            )}
          </div>

          <IconButtonToggle open={menuOpen} onToggle={() => setMenuOpen((open) => !open)} />
        </div>

        {menuOpen ? (
          <div className="site-nav-mobile" role="dialog" aria-modal="true" aria-label="Site menu">
            <ul className="site-nav-mobile-list">
              <li>
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="site-nav-mobile-link site-nav-mobile-link--icon"
                  aria-label="GitHub"
                  onClick={() => setMenuOpen(false)}
                >
                  <GithubMark size={25} />
                </a>
              </li>
              <li>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="site-nav-mobile-link site-nav-mobile-link--icon"
                  aria-label="Discord"
                  onClick={() => setMenuOpen(false)}
                >
                  <DiscordMark size={25} />
                </a>
              </li>
              {PUBLIC_LINKS.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    className="site-nav-mobile-link"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="site-nav-mobile-actions">
              {canOpenConsole ? (
                <Link
                  to="/console"
                  className="btn btn--primary btn--block"
                  onClick={() => setMenuOpen(false)}
                >
                  Open Console
                </Link>
              ) : (
                <>
                  {!user ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--block"
                      onClick={() => {
                        setMenuOpen(false);
                        setSignInOpen(true);
                      }}
                      disabled={loading}
                    >
                      Sign In
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--ghost btn--block"
                      onClick={() => void handleSignOut()}
                      disabled={signingOut}
                    >
                      {signingOut ? "Signing Out…" : "Sign Out"}
                    </button>
                  )}
                  <Link
                    to="/sign-up"
                    className="btn btn--primary btn--block"
                    onClick={() => setMenuOpen(false)}
                  >
                    Request Access
                  </Link>
                </>
              )}
            </div>
          </div>
        ) : null}
        {signOutError ? (
          <p className="form-error" role="alert">
            Could not sign out. Please try again.
          </p>
        ) : null}
      </header>
      <main id="main" className="site-main">
        <Outlet />
      </main>
      <footer className="site-footer">
        <div className="site-footer-inner">
          <p>&copy; {new Date().getFullYear()} Woven. All rights reserved.</p>
        </div>
      </footer>

      {signInOpen ? <SignInModal onClose={() => setSignInOpen(false)} /> : null}
      <ScrollRestoration />
    </div>
  );
}

function IconButtonToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="icon-button site-nav-toggle"
      aria-label={open ? "Close menu" : "Open menu"}
      aria-expanded={open}
      onClick={onToggle}
    >
      {open ? (
        <X size={20} strokeWidth={1.7} absoluteStrokeWidth aria-hidden="true" />
      ) : (
        <Menu size={20} strokeWidth={1.7} absoluteStrokeWidth aria-hidden="true" />
      )}
    </button>
  );
}
